/**
 * Reactive settings backed by localStorage.
 *
 * Mirrors the native app's scattered `@AppStorage` / `UserDefaults`
 * lookups (`isKokoMemoryEnabled`, `kokoNestIdleMinutes`, etc.) but
 * keeps everything in one typed store so we can subscribe, persist, and
 * reset uniformly.
 *
 * Keys are namespaced with `koko_` so they don't collide with host-site
 * localStorage entries. Values are JSON-encoded so booleans, numbers,
 * and string enums round-trip cleanly.
 */

import type { KokoConfig } from "../types";
import { DEFAULT_VOICE_KEY, VOICE_CANDIDATES } from "../output/voices";

export type CursorVisibilityMode = "always" | "transient";
export type ModelChoice = "sonnet" | "opus";

export interface KokoSettings {
  sessionActive: boolean;
  voiceEnabled: boolean;
  textOnlyMode: boolean;
  wakeWordEnabled: boolean;
  cursorVisibility: CursorVisibilityMode;
  sfxEnabled: boolean;
  memoryEnabled: boolean;
  proactiveEnabled: boolean;
  proactiveIntervalMinutes: number;
  nestEnabled: boolean;
  nestIdleMinutes: number;
  webSearchEnabled: boolean;
  birdStyle: string;
  /**
   * Catalog `key` of the voice candidate Koko speaks with. The actual
   * ElevenLabs voice ID + per-candidate playback-rate live in
   * `VOICE_CANDIDATES`; we persist a stable `key` so two entries can
   * share an underlying voice ID with different pitch settings.
   */
  voiceId: string;
  model: ModelChoice;
}

const STORAGE_KEYS: Record<keyof KokoSettings, string> = {
  sessionActive: "koko_session_active",
  voiceEnabled: "koko_voice_enabled",
  textOnlyMode: "koko_text_only",
  wakeWordEnabled: "koko_wake_enabled",
  cursorVisibility: "koko_cursor_visibility",
  sfxEnabled: "koko_sfx_enabled",
  memoryEnabled: "koko_memory_enabled",
  proactiveEnabled: "koko_proactive_enabled",
  proactiveIntervalMinutes: "koko_proactive_interval_minutes",
  nestEnabled: "koko_nest_enabled",
  nestIdleMinutes: "koko_nest_idle_minutes",
  webSearchEnabled: "koko_web_search_enabled",
  birdStyle: "koko_bird_style",
  voiceId: "koko_voice_id",
  model: "koko_model",
};

/**
 * Default values. Proactive observations default ON per the user's
 * explicit choice in planning; every other default matches native.
 */
export function buildDefaultSettings(
  config: KokoConfig,
  birdStyle: string,
): KokoSettings {
  return {
    sessionActive: true,
    voiceEnabled: config.voice,
    textOnlyMode: false,
    wakeWordEnabled: false,
    cursorVisibility: "always",
    sfxEnabled: true,
    memoryEnabled: true,
    proactiveEnabled: true,
    proactiveIntervalMinutes: 5,
    nestEnabled: true,
    nestIdleMinutes: 3,
    // Web search defaults ON — Claude only invokes the tool when the
    // user's question actually needs fresh info, so leaving it on
    // doesn't add cost or latency to the common no-search case.
    webSearchEnabled: true,
    birdStyle,
    voiceId: DEFAULT_VOICE_KEY,
    // Opus by default; the host can still force sonnet via
    // `data-model="sonnet"` on the embed tag.
    model: config.model === "sonnet" ? "sonnet" : "opus",
  };
}

type SettingsListener<K extends keyof KokoSettings> = (
  value: KokoSettings[K],
  previous: KokoSettings[K],
) => void;

export class SettingsStore {
  private values: KokoSettings;
  private readonly listeners = new Map<keyof KokoSettings, Set<SettingsListener<keyof KokoSettings>>>();

  constructor(defaults: KokoSettings) {
    this.values = { ...defaults, ...this.readPersisted(defaults) };
  }

  get<K extends keyof KokoSettings>(key: K): KokoSettings[K] {
    return this.values[key];
  }

  set<K extends keyof KokoSettings>(key: K, nextValue: KokoSettings[K]): void {
    const previousValue = this.values[key];
    if (previousValue === nextValue) return;
    this.values[key] = nextValue;
    this.persist(key, nextValue);
    const keyListeners = this.listeners.get(key);
    if (keyListeners) {
      keyListeners.forEach((listener) =>
        (listener as SettingsListener<K>)(nextValue, previousValue),
      );
    }
  }

  subscribe<K extends keyof KokoSettings>(
    key: K,
    listener: SettingsListener<K>,
  ): () => void {
    let keyListeners = this.listeners.get(key);
    if (!keyListeners) {
      keyListeners = new Set();
      this.listeners.set(key, keyListeners);
    }
    keyListeners.add(listener as SettingsListener<keyof KokoSettings>);
    return () => {
      keyListeners!.delete(listener as SettingsListener<keyof KokoSettings>);
    };
  }

  snapshot(): KokoSettings {
    return { ...this.values };
  }

  private readPersisted(defaults: KokoSettings): Partial<KokoSettings> {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const result: Partial<KokoSettings> = {};
    (Object.keys(STORAGE_KEYS) as (keyof KokoSettings)[]).forEach((key) => {
      const raw = window.localStorage.getItem(STORAGE_KEYS[key]);
      if (raw === null) return;
      try {
        const parsed = JSON.parse(raw);
        if (isValidValueForKey(key, parsed, defaults[key])) {
          (result as Record<string, unknown>)[key] = parsed;
        }
      } catch {
        // Corrupt entry — fall back to the default by leaving it unset.
      }
    });
    return result;
  }

  private persist<K extends keyof KokoSettings>(key: K, value: KokoSettings[K]): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(STORAGE_KEYS[key], JSON.stringify(value));
    } catch {
      // Quota / privacy-mode Safari — silently ignore; settings become
      // per-tab instead of persistent.
    }
  }
}

/**
 * Narrow type check so a corrupted localStorage entry (say, a string
 * where a number is expected) doesn't poison the running settings.
 */
function isValidValueForKey<K extends keyof KokoSettings>(
  key: K,
  candidate: unknown,
  fallback: KokoSettings[K],
): candidate is KokoSettings[K] {
  if (typeof fallback === typeof candidate) {
    if (typeof fallback === "number") {
      return Number.isFinite(candidate);
    }
    if (key === "cursorVisibility") {
      return candidate === "always" || candidate === "transient";
    }
    if (key === "model") {
      return candidate === "sonnet" || candidate === "opus";
    }
    if (key === "voiceId") {
      // Reject stored voice keys that aren't in our curated list — a
      // candidate gets removed and we shouldn't keep its stale key
      // around. Falls back to the default at load time.
      return VOICE_CANDIDATES.some((entry) => entry.key === candidate);
    }
    return true;
  }
  return false;
}
